import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'react-toastify';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { setAuthUser } from '../utils/userData';
import './job-signup.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const JobSignup = () => {
	const navigate = useNavigate();
	const [formData, setFormData] = useState({
		fullName: '',
		email: '',
		password: '',
		confirmPassword: ''
	});
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSignup = async (e) => {
		e.preventDefault();

		if (!formData.fullName || !formData.email || !formData.password || !formData.confirmPassword) {
			toast.error('Please complete all fields.');
			return;
		}

		if (formData.password.length < 6) {
			toast.error('Password must be at least 6 characters long.');
			return;
		}

		if (formData.password !== formData.confirmPassword) {
			toast.error('Passwords do not match.');
			return;
		}

		try {
			setIsSubmitting(true);
			await axios.post(`${API_BASE_URL}/job-signup`, {
				fullName: formData.fullName,
				email: formData.email,
				password: formData.password
			});

			setAuthUser({
				email: formData.email,
				fullName: formData.fullName,
				loginType: 'signup'
			});

			toast.success('Account created and signed in successfully.');
			navigate('/jobs');
		} catch (error) {
			const errorMessage =
				error.response?.data?.message || 'Unable to create account. Please try again.';
			toast.error(errorMessage);
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="signup-page">
			<Navbar />

			<section className="signup-section">
				<motion.div
					className="signup-glow"
					animate={{ scale: [1, 1.08, 1], opacity: [0.45, 0.8, 0.45] }}
					transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
				/>

				<motion.div
					className="signup-card"
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6, ease: 'easeOut' }}
				>
					<motion.h1
						className="signup-title"
						initial={{ opacity: 0, y: 16 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.2, duration: 0.5 }}
					>
						Create Your Member Account
					</motion.h1>

					<motion.p
						className="signup-subtitle"
						initial={{ opacity: 0, y: 16 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.3, duration: 0.5 }}
					>
						Join the premium talent network and unlock exclusive roles.
					</motion.p>

					<motion.form
						className="signup-form"
						onSubmit={handleSignup}
						initial={{ opacity: 0, y: 16 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ delay: 0.4, duration: 0.5 }}
					>
						<label htmlFor="fullName">Full Name</label>
						<input
							id="fullName"
							name="fullName"
							type="text"
							value={formData.fullName}
							onChange={handleChange}
							placeholder="Your full name"
							autoComplete="name"
						/>

						<label htmlFor="email">Email</label>
						<input
							id="email"
							name="email"
							type="email"
							value={formData.email}
							onChange={handleChange}
							placeholder="you@example.com"
							autoComplete="email"
						/>

						<label htmlFor="password">Password</label>
						<input
							id="password"
							name="password"
							type="password"
							value={formData.password}
							onChange={handleChange}
							placeholder="At least 6 characters"
							autoComplete="new-password"
						/>

						<label htmlFor="confirmPassword">Confirm Password</label>
						<input
							id="confirmPassword"
							name="confirmPassword"
							type="password"
							value={formData.confirmPassword}
							onChange={handleChange}
							placeholder="Re-enter your password"
							autoComplete="new-password"
						/>

						<button className="signup-submit" type="submit" disabled={isSubmitting}>
							{isSubmitting ? 'Creating Account...' : 'Create Account'}
						</button>
					</motion.form>

					<motion.p
						className="signup-meta"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.55, duration: 0.5 }}
					>
						Already have an account? <Link to="/job-login">Sign in here</Link>
					</motion.p>
				</motion.div>
			</section>

			<Footer />
		</div>
	);
};

export default JobSignup;
